import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDown, Search, Check } from 'lucide-react';

export interface AdminSelectOption {
  value: string;
  label: string;
  sublabel?: string;
}

interface AdminSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: AdminSelectOption[];
  placeholder?: string;
  disabled?: boolean;
  searchable?: boolean;
  className?: string;
}

export function AdminSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  searchable = false,
  className = '',
}: AdminSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function openDropdown() {
    if (disabled) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
    setTimeout(() => searchRef.current?.focus(), 0);
  }

  function close() {
    setOpen(false);
    setQuery('');
  }

  function handleSelect(val: string) {
    onChange(val);
    close();
  }

  const selected = options.find((o) => o.value === value);
  const filtered = searchable && query.trim()
    ? options.filter((o) =>
        o.label.toLowerCase().includes(query.toLowerCase()) ||
        o.sublabel?.toLowerCase().includes(query.toLowerCase())
      )
    : options;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={openDropdown}
        disabled={disabled}
        className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500
          ${open
            ? 'border-blue-500 ring-2 ring-blue-500 bg-white dark:bg-gray-900'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          text-gray-900 dark:text-gray-100`}
      >
        <span className={`truncate ${!selected ? 'text-gray-400 dark:text-gray-500' : ''}`}>
          {selected ? (
            <span className="flex items-center gap-1.5">
              {selected.label}
              {selected.sublabel && (
                <span className="text-gray-400 dark:text-gray-500 text-xs">{selected.sublabel}</span>
              )}
            </span>
          ) : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ ...dropdownStyle, position: 'fixed', zIndex: 9999 }}
          className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
        >
          {searchable && (
            <div className="p-2 border-b border-gray-100 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search…"
                  className="w-full pl-8 pr-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') close();
                    if (e.key === 'Enter' && filtered.length === 1) handleSelect(filtered[0].value);
                  }}
                />
              </div>
            </div>
          )}
          <div className="overflow-y-auto" style={{ maxHeight: 240 }}>
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-sm text-gray-400 dark:text-gray-500 text-center">No options</div>
            )}
            {filtered.map((opt) => (
              <button
                key={opt.value}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSelect(opt.value)}
                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="text-sm text-gray-900 dark:text-gray-100 truncate">{opt.label}</span>
                  {opt.sublabel && (
                    <span className="text-xs text-gray-400 dark:text-gray-500 shrink-0">{opt.sublabel}</span>
                  )}
                </span>
                {opt.value === value && <Check className="w-4 h-4 text-blue-500 shrink-0" />}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
